import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import debounce from 'lodash/debounce';
import {
  CentBasedInputCurrency,
  DateRangePicker,
  Empty,
  EmptyTable,
  FieldWrapper,
  Filter,
  FilterButton,
  HeaderCell,
  HeaderRow,
  ModalFactory,
  PaginatedList,
  Section,
  SectionHeader,
  Select,
  SortLabel,
  Table,
  TableBody,
  TableHead,
} from 'components';
import { DDOption } from 'config/const';
import { getQueryStringValue, useQueryString } from 'hooks';
import { AchTransactionDetails } from 'modals';
import { routeConstants } from 'routes';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { Ach, getSiteAchTransactionsRequest, selectAchTransactions } from 'store/Collections';
import { selectCurrentEmployeeRole } from 'store/Employers';
import { selectSiteSettings } from 'store/Site';
import { IStore } from 'store/types';
import { AchPaymentType, AchStatus, AchType, ApiQueryOperator, IAchTransaction, IApiQueryParam, SiteRole } from 'types';
import { formatDateForApi } from 'utils/date';
import { capitalize } from 'utils/strings';
import { AchTransactionRow } from './components';

const DEFAULT_START_DATE = formatDateForApi(dayjs().subtract(6, 'days'));
const DEFAULT_END_DATE = formatDateForApi(dayjs().add(1, 'day'));

const buildOptions = (options?: string[]) => options?.map((status: string) => ({ value: status, label: capitalize(status) })) || [];
const allOption: DDOption = { label: 'All', value: undefined };
const paymentTypeOptions = [allOption, ...buildOptions(Object.values(AchPaymentType))];
const achStatusOptions = [allOption, ...buildOptions(Object.values(AchStatus))];

const ReportsAchHistory = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [filterVisible, setFilterVisible] = useState<boolean>(false);
  const [currentTransaction, setCurrentTransaction] = useState<IAchTransaction>();
  const currentSiteId = useSelector(selectCurrentEmployee())?.siteId || '';
  const currentSiteSettings = useSelector(selectSiteSettings());
  const currentEmployeeRole = useSelector(selectCurrentEmployeeRole);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Ach.GET_TRANSACTIONS_REQUEST));
  const transactions = useSelector(selectAchTransactions());

  const [urlQuery, setUrlQuery] = useQueryString('achHistory', window.location, {
    q: [
      { key: 'created_at', operator: ApiQueryOperator.gte, value: DEFAULT_START_DATE },
      { key: 'created_at', operator: ApiQueryOperator.lt, value: DEFAULT_END_DATE },
    ],
    sort: '-created_at',
    limit: 5,
  });

  const amountSearch = getQueryStringValue(urlQuery.q, 'amount', ApiQueryOperator.eq) as string;
  const [amount, setAmount] = useState(amountSearch);

  const onDateRangeChange = (startDate: string, endDate: string) => {
    const query = (urlQuery.q || []).filter((item: IApiQueryParam) => item.key !== 'created_at');
    query.push({ key: 'created_at', operator: ApiQueryOperator.gte, value: formatDateForApi(startDate) }, { key: 'created_at', operator: ApiQueryOperator.lt, value: formatDateForApi(dayjs(endDate).add(1, 'day')) });
    setUrlQuery({ q: query, offset: 0 });
  };

  const onFilterChange = useCallback(
    (value: string | DDOption[], key: string) => {
      const otherFilters = (urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== key);
      const newValue = typeof value === 'string' ? (Number(value) !== 0 ? value.trim() : '') : value[0]?.value;
      const filters = newValue ? [...otherFilters, { key, operator: ApiQueryOperator.eq, value: newValue }] : otherFilters;
      setUrlQuery({ q: filters, offset: 0 });
    },
    [urlQuery]
  );
  const debouncedOnFilterChange = useCallback(debounce(onFilterChange, 600), [onFilterChange]);

  const isSortedBy = (headerValue: string) => urlQuery.sort === headerValue || urlQuery.sort === `-${headerValue}`;
  const isSortDesc = urlQuery.sort?.[0] === '-';
  const onSortChange = (key: string) => {
    setUrlQuery({ sort: urlQuery.sort === key ? `-${urlQuery.sort}` : key });
  };

  const startDate = getQueryStringValue(urlQuery.q, 'created_at', ApiQueryOperator.gte) as string;
  const endDate = getQueryStringValue(urlQuery.q, 'created_at', ApiQueryOperator.lt) as string;
  const paymentTypeSearch = getQueryStringValue(urlQuery.q, 'paymentType', ApiQueryOperator.eq) as string;
  const statusSearch = getQueryStringValue(urlQuery.q, 'status', ApiQueryOperator.eq) as string;
  const qWithoutDates = (urlQuery.q || []).filter((q: IApiQueryParam) => q.key !== 'created_at');

  useEffect(() => {
    if (!currentSiteId || (currentSiteSettings && currentSiteSettings.achType !== AchType.KICKFIN) || (currentEmployeeRole && currentEmployeeRole !== SiteRole.ADMIN)) {
      history.push(routeConstants.private.reportsPaidPayments);
    }
  }, [history, currentSiteId, currentSiteSettings, currentEmployeeRole]);

  useEffect(() => {
    if (currentSiteSettings?.achType === AchType.KICKFIN && currentEmployeeRole === SiteRole.ADMIN) {
      dispatch(getSiteAchTransactionsRequest(currentSiteId, urlQuery));
    }
  }, [dispatch, currentSiteSettings, currentEmployeeRole, currentSiteId, urlQuery]);

  const [showAchModal, hideAchModal] = useModal(
    () => (
      <ModalFactory customModal onClose={hideAchModal} isActive>
        <AchTransactionDetails transaction={currentTransaction} onClose={hideAchModal} />
      </ModalFactory>
    ),
    [currentTransaction]
  );

  return (
    <Section data-cy="table">
      <SectionHeader isLoading={isLoading || !transactions} title="Results" value={`(${transactions?.total})`} data-cy="ach-history-content-header">
        <DateRangePicker
          data-cy="date-picker"
          fluidWidth={false}
          inputHeight="small"
          onSubmit={(dates) => onDateRangeChange(dates[0], dates[1])}
          pickerPosition="right"
          startDate={startDate}
          endDate={formatDateForApi(dayjs(endDate).subtract(1, 'day'))}
          submitLabel="apply"
        />
        <FilterButton filterActive={filterVisible} appliedFilters={qWithoutDates.length} onClick={() => setFilterVisible(!filterVisible)} />
      </SectionHeader>

      <Filter isVisible={filterVisible}>
        <FieldWrapper label="Type" data-cy="type-filter">
          <Select placeholder="Select" options={paymentTypeOptions} values={buildOptions([paymentTypeSearch])} onChange={(selected) => onFilterChange(selected, 'paymentType')} />
        </FieldWrapper>
        <FieldWrapper label="ACH Status" data-cy="status-filter">
          <Select placeholder="Select" options={achStatusOptions} values={buildOptions([statusSearch])} onChange={(selected) => onFilterChange(selected, 'status')} />
        </FieldWrapper>
        <FieldWrapper label="Amount" data-cy="amount-filter">
          <CentBasedInputCurrency
            maxCurrencyLength={8}
            value={amount}
            onChange={(val: string) => {
              setAmount(val);
              debouncedOnFilterChange(val, 'amount');
            }}
          />
        </FieldWrapper>
      </Filter>

      <PaginatedList total={transactions?.total} limit={urlQuery?.limit} offset={urlQuery?.offset} onPageChange={setUrlQuery} isLoading={isLoading || !transactions} standalone={false}>
        <Table scrollable>
          <TableHead>
            <HeaderRow sticky>
              <HeaderCell data-cy="date-header" onClick={() => onSortChange('created_at')}>
                <SortLabel label="Date Entered" isSorted={isSortedBy('created_at')} isSortedDesc={isSortDesc} isLoading={isLoading} />
              </HeaderCell>
              <HeaderCell data-cy="id-header">{!isLoading && 'Transaction ID'}</HeaderCell>
              <HeaderCell data-cy="desc-header">{!isLoading && 'Description'}</HeaderCell>
              <HeaderCell data-cy="type-header">{!isLoading && 'Type'}</HeaderCell>
              <HeaderCell data-cy="status-header">{!isLoading && 'ACH Status'}</HeaderCell>
              <HeaderCell data-cy="amount-header">{!isLoading && 'Amount'}</HeaderCell>
            </HeaderRow>
          </TableHead>
          <TableBody>
            {!isLoading && transactions?.total === 0 && (
              <EmptyTable>
                <Empty icon="Payment" message="No transactions. Try a different date range." />
              </EmptyTable>
            )}
            {isLoading
              ? [...Array(5)].map((_, i) => <AchTransactionRow key={i} isLoading />)
              : transactions?.data.map((transaction) => (
                  <AchTransactionRow
                    key={transaction.id}
                    transaction={transaction}
                    onClick={() => {
                      setCurrentTransaction(transaction);
                      showAchModal();
                    }}
                    isLoading={isLoading}
                    data-cy="ach-transaction-row"
                  />
                ))}
          </TableBody>
        </Table>
      </PaginatedList>
    </Section>
  );
};
export default ReportsAchHistory;
